import firebase from '../firebaseConfig';
import { PublicProfileKeys } from '../rtdbKeys';

class User {
    constructor(id, firstName, lastName, artistIds) {
        this.id = id;
        this.firstName = firstName;
        this.lastName = lastName;
        this.artistIds = artistIds;
    }

    static async getUserById(userId) {
        const snapshot = await firebase.database().ref("users").child(userId).once("value");
        const user = snapshot.val();

        const artistKeys = user.artists ? Object.keys(user.artists) : [];

        return new User(
            userId,
            user.metadata["first name"],
            user.metadata["last name"],
            artistKeys
        );
    }

    // Cross-user readable display name. Reads /users/{uid}/publicProfile —
    // any authenticated user can read this subtree, so use it for foreign-uid
    // lookups (member lists, share-link viewer dashboards, comment authors)
    // where the locked-down /users/{uid}/metadata is not accessible.
    static async getPublicProfile(userId) {
        const snap = await firebase.database()
            .ref('users').child(userId)
            .child(PublicProfileKeys.publicProfile)
            .once('value');
        const val = snap.val();
        if (!val) return null;
        return {
            firstName: val[PublicProfileKeys.firstName] || '',
            lastName: val[PublicProfileKeys.lastName] || '',
        };
    }

    static async getUserName(userId) {
        const profile = await User.getPublicProfile(userId);
        const first = (profile?.firstName || '').trim();
        const lastInitial = (profile?.lastName || '').trim()[0] || '';
        return `${first}${lastInitial ? ` ${lastInitial}` : ''}`;
    }

    static async getUserInitials(userId) {
        const profile = await User.getPublicProfile(userId);
        const firstInitial = (profile?.firstName || '').trim()[0] || '';
        const lastInitial = (profile?.lastName || '').trim()[0] || '';
        return firstInitial + lastInitial;
    }

    // Writes the display name to both /metadata and the cross-user-readable
    // /publicProfile so foreign-uid lookups stay in sync.
    static async setName(uid, firstName, lastName) {
        const userRef = firebase.database().ref("users").child(uid);
        await userRef.child("metadata").update({
            "first name": firstName,
            "last name": lastName,
        });
        await userRef.child(PublicProfileKeys.publicProfile).update({
            [PublicProfileKeys.firstName]: firstName,
            [PublicProfileKeys.lastName]: lastName,
        });
    }

    static async ensureUserRecord(authUser) {
        const userRef = firebase.database().ref("users").child(authUser.uid);
        const ref = userRef.child("metadata");
        const snap = await ref.once("value");
        if (snap.exists()) return false;

        // `email` and `signup provider` are server-owned: the userWasCreated
        // Auth trigger seeds them from the Auth record, so the client must
        // not write them. The client seeds only what the trigger can't
        // derive — a provider-supplied name + the migration flag.
        //
        // Only seed names the auth provider actually supplied — never
        // fabricate one from the email prefix. A missing name routes the
        // user into the onboarding name step (parity with iOS).
        const displayName = (authUser.displayName || "").trim();
        const parts = displayName ? displayName.split(/\s+/) : [];
        const firstName = parts[0] || "";
        const lastName = parts.length > 1 ? parts.slice(1).join(" ") : "";

        await ref.set({
            "time created": Date.now(),
            "first name": firstName,
            "last name": lastName,
            migrations: { hiddenStorage: true },
        });
        // Fan out to /publicProfile so foreign-uid display name lookups
        // work without needing the legacy metadata read. Mirrors iOS
        // User.setName + signup flow.
        await userRef.child(PublicProfileKeys.publicProfile).set({
            [PublicProfileKeys.firstName]: firstName,
            [PublicProfileKeys.lastName]: lastName,
        });
        return true;
    }
}

export default User;
